import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardBody, Alert, Spinner, Button } from '@kollektive/design-system';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useInvitationValidation, acceptInvitation } from '@/hooks/useInvitations';
import styles from './AcceptInvitationPage.module.css';

// Session storage key for invitation flow
export const INVITATION_FLOW_KEY = 'auth_invitation_flow';

// Google icon SVG
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

// Microsoft icon SVG
function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

export function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { session, isLoading: isAuthLoading } = useAuth();
  const { validation, isLoading: isValidating, error: validationError } = useInvitationValidation(token);

  const [isSigningIn, setIsSigningIn] = useState<'google' | 'microsoft' | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ tenantName: string; subdomainUrl: string } | null>(null);

  // Check if we're returning from OAuth
  useEffect(() => {
    const storedToken = sessionStorage.getItem(INVITATION_FLOW_KEY);
    if (storedToken && session?.access_token && !isAccepting && !success) {
      // Clear the stored token
      sessionStorage.removeItem(INVITATION_FLOW_KEY);
      // Auto-accept the invitation
      handleAccept(storedToken);
    }
  }, [session?.access_token]);

  const handleGoogleSignIn = async () => {
    if (!token) return;
    setIsSigningIn('google');
    setError(null);

    // Store token for after OAuth
    sessionStorage.setItem(INVITATION_FLOW_KEY, token);

    const redirectTo = `${window.location.origin}/invitations/accept?token=${encodeURIComponent(token)}`;

    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });

    if (oauthError) {
      setError(oauthError.message);
      sessionStorage.removeItem(INVITATION_FLOW_KEY);
    }
    setIsSigningIn(null);
  };

  const handleMicrosoftSignIn = async () => {
    if (!token) return;
    setIsSigningIn('microsoft');
    setError(null);

    // Store token for after OAuth
    sessionStorage.setItem(INVITATION_FLOW_KEY, token);

    const redirectTo = `${window.location.origin}/invitations/accept?token=${encodeURIComponent(token)}`;

    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        redirectTo,
        scopes: 'email profile openid',
      },
    });

    if (oauthError) {
      setError(oauthError.message);
      sessionStorage.removeItem(INVITATION_FLOW_KEY);
    }
    setIsSigningIn(null);
  };

  const handleAccept = async (inviteToken?: string) => {
    const tokenToUse = inviteToken || token;
    if (!tokenToUse || !session?.access_token) return;

    setIsAccepting(true);
    setError(null);

    try {
      const response = await acceptInvitation(tokenToUse, session.access_token);
      setSuccess({
        tenantName: response.tenant.name,
        subdomainUrl: response.tenant.subdomain_url,
      });
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('ALREADY_MEMBER')) {
          setError('You are already a member of this workspace.');
        } else if (err.message.includes('INVALID_TOKEN')) {
          setError('This invitation is no longer valid.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to accept invitation');
      }
    } finally {
      setIsAccepting(false);
    }
  };

  // No token provided
  if (!token) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Card>
            <CardBody className={styles.cardBody}>
              <Alert variant="error" styleVariant="glass">
                Invalid invitation link. Please check your email and try again.
              </Alert>
              <Link to="/login" className={styles.link}>
                Go to Login
              </Link>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  // Loading validation
  if (isValidating || isAuthLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.loadingState}>
            <Spinner size="lg" />
            <p>Validating invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  // Validation error
  if (validationError) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Card>
            <CardBody className={styles.cardBody}>
              <Alert variant="error" styleVariant="glass">
                Failed to validate invitation. Please try again later.
              </Alert>
              <Link to="/login" className={styles.link}>
                Go to Login
              </Link>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  // Invalid invitation
  if (validation && !validation.valid) {
    const reasonMessages: Record<string, string> = {
      invalid: 'This invitation link is invalid or has been used.',
      expired: 'This invitation has expired. Please ask for a new invitation.',
      already_accepted: 'This invitation has already been accepted.',
      cancelled: 'This invitation has been cancelled.',
    };

    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Card>
            <CardBody className={styles.cardBody}>
              <Alert variant="warning" styleVariant="glass">
                {reasonMessages[validation.reason || 'invalid'] || 'This invitation is no longer valid.'}
              </Alert>
              <Link to="/login" className={styles.link}>
                Go to Login
              </Link>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.branding}>
            <div className={styles.logoPlaceholder}>
              <span>K</span>
            </div>
            <h1 className={styles.title}>Welcome to {success.tenantName}!</h1>
            <p className={styles.subtitle}>
              Your account has been successfully set up.
            </p>
          </div>

          <Card>
            <CardBody className={styles.cardBody}>
              <Alert variant="success" styleVariant="glass">
                You have successfully joined the workspace.
              </Alert>
              <Button
                onClick={() => window.location.href = success.subdomainUrl}
                className={styles.continueButton}
              >
                Continue to Dashboard
              </Button>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  // Valid invitation - show details and sign in/accept
  const isButtonDisabled = isSigningIn !== null || isAccepting;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Logo and branding */}
        <div className={styles.branding}>
          <div className={styles.logoPlaceholder}>
            <span>K</span>
          </div>
          <h1 className={styles.title}>Join {validation?.tenant_name}</h1>
          <p className={styles.subtitle}>
            You've been invited to join as {/^[aeiou]/i.test(validation?.role || '') ? 'an' : 'a'} {validation?.role}.
          </p>
        </div>

        {/* Invitation card */}
        <Card>
          <CardBody className={styles.cardBody}>
            {/* Error message */}
            {error && (
              <Alert variant="error" styleVariant="glass" className={styles.error}>
                {error}
              </Alert>
            )}

            {session?.access_token ? (
              // User is authenticated - show accept button
              <>
                <p className={styles.signedInAs}>
                  Signed in as <strong>{session.user?.email}</strong>
                </p>
                <Button
                  onClick={() => handleAccept()}
                  disabled={isAccepting}
                  className={styles.acceptButton}
                >
                  {isAccepting ? 'Joining...' : 'Accept Invitation'}
                </Button>
                <p className={styles.wrongAccount}>
                  Not the right account?{' '}
                  <button
                    type="button"
                    onClick={async () => {
                      await supabase.auth.signOut();
                      window.location.reload();
                    }}
                    className={styles.linkButton}
                  >
                    Sign out
                  </button>
                </p>
              </>
            ) : (
              // User is not authenticated - show OAuth buttons
              <>
                <p className={styles.signInPrompt}>
                  Sign in to accept this invitation
                </p>

                <div className={styles.oauthButtons}>
                  <button
                    onClick={handleGoogleSignIn}
                    disabled={isButtonDisabled}
                    className={styles.googleButton}
                  >
                    {isSigningIn === 'google' ? (
                      <Spinner size="sm" />
                    ) : (
                      <GoogleIcon className={styles.oauthIcon} />
                    )}
                    <span>Continue with Google</span>
                  </button>

                  <button
                    onClick={handleMicrosoftSignIn}
                    disabled={isButtonDisabled}
                    className={styles.microsoftButton}
                  >
                    {isSigningIn === 'microsoft' ? (
                      <Spinner size="sm" />
                    ) : (
                      <MicrosoftIcon className={styles.oauthIcon} />
                    )}
                    <span>Continue with Microsoft</span>
                  </button>
                </div>
              </>
            )}
          </CardBody>
        </Card>

        {/* Powered by */}
        <p className={styles.poweredBy}>
          Powered by <span className={styles.poweredByBrand}>Kollektive</span>
        </p>
      </div>
    </div>
  );
}
